// =============================================================================
// Caller-name + dispatcher-note recovery from TripLink's raw trip note
//
// Pure, deterministic helpers that recover the invoice "Caller's Name"
// (col D) and "Notes" (col G) from TripLink's `fleet_trips.note` string
// (schema field `trip.note`). Derived from + validated against n8n's actual
// invoice output (Invoice MSMC-2026-003, 1006 joined trips):
//
//   parseRequestedByFromNote(note, true)  : 92.3% exact / 95.8% case-insensitive,
//                                           98.9% recovery of n8n's non-blank callers.
//   parseRequestedByFromNote(note, false) : 87.4% exact, 0 false positives.
//   cleanInvoiceNotes                     : recovers note CONTENT (col G is
//                                           LLM-rewritten upstream, so exact
//                                           phrasing parity is impossible).
//
// Used by the TripLink sync to fill `bookings.requested_by` + `dispatch_note`
// for synced rows ONLY, fill-empty-only — portal (V2-form) bookings keep
// their native requester + pickup notes. Kept in shared so the API (and any
// future UI) use one implementation. NO pricing/rate logic lives here.
//
// The function BODIES are the validated derivation verbatim; only the
// STOP-word sets are hardened (see the "hardened" notes) so a department /
// clinical-service / role word is never surfaced as a caller name on a client
// invoice — zero wrong names matters more than the last few % of fill.
// =============================================================================

// US phone numbers: (305) 674-2177 | 3054410910 | 305 535 3300 | 305-674-2570
const PHONE = /\(?\d{3}\)?[\s.\-]*\d{3}[\s.\-]*\d{2,4}|\b\d{9,11}\b/;
const PHONE_G = /\(?\d{3}\)?[\s.\-]*\d{3}[\s.\-]*\d{2,4}|\b\d{9,11}\b/g;

// Tokens that are never a requester first name.
const ROLE_STOP = new Set([
  "ph", "ext", "case", "care", "manager", "nurse", "nuerse", "scheduler", "scheduling",
  "reservationist", "department", "departament", "dept", "dep", "building", "bldg", "companion",
  "pass", "passenger", "room", "floor", "main", "tower", "center", "centre", "pavilion", "suite",
  "apt", "app", "appt", "appointment", "welcome", "solo", "total", "need", "needs", "provide",
  "wheelchair", "wc", "req", "request", "requested", "by", "re", "name", "number", "booked",
  "scheduled", "with", "from", "lobby", "do", "pu", "drop", "pick", "off", "up", "at", "the", "for",
  // hardened (1.20.10): clinical roles / non-name nouns occasionally captured
  // as a caller name. (Titles like "dr"/"md"/"rn" are deliberately NOT here —
  // they legitimately precede a real name, e.g. "Dr Smith".)
  "patient", "family", "caregiver", "aide", "social", "worker", "facility", "hospital",
  "office", "reception", "receptionist", "secretary", "assistant", "tech", "technician",
  "transport", "dispatch", "dispatcher", "driver", "vendor", "front", "desk", "unit", "ward",
  "bed", "clinic", "lab", "laboratory",
]);
const DEPT_STOP = new Set([
  "urology", "cardiology", "cardiosurgery", "pulmonary", "pulmonology", "pulmonoly", "oncology",
  "recovery", "primary", "imed", "neurosurgery", "neurosurgeon", "orthopedics", "orthopedic",
  "orthopheidc", "gastroenterology", "colorectal", "behavioral", "vascular", "gi", "bhlt",
  "institute", "ambulatory", "neurology", "radiology", "nephrology", "dermatology", "endocrinology",
  "hematology", "rheumatology", "gynecology", "obstetrics", "psychiatry", "oncologist",
  "management", "coordinator", "station", "medical", "cancer", "health", "physicians",
  // hardened (1.20.10): additional department / clinical-service words so they
  // never surface as a caller name on a client invoice.
  "surgery", "surgical", "transplant", "infusion", "dialysis", "rehab", "rehabilitation",
  "therapy", "anesthesia", "anesthesiology", "ophthalmology", "opthalmology", "podiatry",
  "otolaryngology", "hepatology", "endoscopy", "mammography", "bariatric", "geriatrics",
  "pediatrics", "pediatric", "maternity", "hospitalist", "radiation", "chemotherapy",
  "pharmacy", "allergy", "immunology", "urgent", "nursing", "hospice", "emergency", "icu", "pacu",
]);
const STOP = new Set<string>([...ROLE_STOP, ...DEPT_STOP]);

// Clinical / mobility / instruction nouns that are NEVER a requester's first
// name. Used ONLY by the cleaner's free-text requester strip (below) so an
// instruction that happens to follow a bare "req" — "req w/c", "req stretcher",
// "req O2", "req walker" — keeps its meaning instead of being eaten as a name.
// parseRequestedByFromNote is deliberately NOT widened with these (it stays on
// STOP, so a real surname like "Walker"/"Bell" can still be captured there).
const CLINICAL_STOP = new Set<string>([
  "stretcher", "gurney", "oxygen", "walker", "cane", "crutches", "hoyer",
  "wheelchair", "wc", "bariatric", "ambulatory", "assist", "lift", "isolation",
  "portable", "stat", "asap",
]);

const NAME_TOKEN = /^[A-Za-z][A-Za-z'.\-]*$/;
const RB_LINE = /Requested\s*By\s*:\s*([^\n]*)/i;
const KEYCUT = /\b(?:Requester\s*Email|MRN|Cost\s*Center|Department|PU\s*Notes|DO\s*Notes|Pickup\s*Location)\s*:/i;
const MARKER = /(?:requested\s*by|request\s*by|req\.?\s*by|booked\s*by|booked\s*with|scheduled\s*by|reservationist|requested|req\.?\s*:|req\.|req\b)[\s:.\-]*/i;
const PHCUT = /\bph\s*#?/i;
const TRIM_EDGES = /^[\s/|\-]+|[\s/|\-]+$/g;

function titleToken(w: string): string {
  return w ? w[0]!.toUpperCase() + w.slice(1).toLowerCase() : w;
}
// n8n preserves ALL-CAPS names ("LUIS SANCHEZ") but Title-Cases everything
// else ("ashlee" -> "Ashlee", "marjorie vindel" -> "Marjorie Vindel").
function applyCase(name: string): string {
  const letters = name.replace(/[^A-Za-z]/g, "");
  if (letters && letters === letters.toUpperCase()) return name;
  return name.split(/\s+/).map(titleToken).join(" ");
}
// Reject phones / initials-only ("DL") / non-names.
function looksLikeName(s: string): boolean {
  const letters = s.replace(/[^A-Za-z]/g, "");
  return letters.length >= 3 && /[aeiou]/i.test(letters);
}
function takeNameTokens(seg: string): string {
  const m = PHONE.exec(seg);
  if (m) seg = seg.slice(0, m.index);
  seg = seg.split(/[/|,;:#\n()]/)[0] ?? "";
  const out: string[] = [];
  for (const t of seg.split(/\s+/).filter(Boolean)) {
    const tl = t.toLowerCase().replace(/\.+$/, "");
    if (/\d/.test(t) || STOP.has(tl) || !NAME_TOKEN.test(t)) break;
    out.push(t);
    if (out.length >= 3) break;
  }
  return out.join(" ").replace(TRIM_EDGES, "");
}

/**
 * Extract the requester NAME from a raw TripLink note. Returns "" when none.
 * @param aggressive when true (default) also infers the name from a
 *        phone-adjacent token in marker-less free text (higher recall). Set
 *        false to mirror n8n exactly with zero false positives.
 */
export function parseRequestedByFromNote(
  note: string,
  aggressive = true,
): string {
  if (!note || !note.trim()) return "";

  // 1) Structured / inline "Requested By:" (portal serialization).
  const rb = RB_LINE.exec(note);
  if (rb) {
    const head = (rb[1] ?? "").split(KEYCUT)[0] ?? "";
    const val = head.split(PHCUT)[0] ?? "";
    const cand = takeNameTokens(val);
    return looksLikeName(cand) ? applyCase(cand) : "";
  }

  // 2) Free-text marker: "req by X", "requested by X", "booked by X", ...
  const mk = MARKER.exec(note);
  if (mk) {
    const seg = note.slice(mk.index + mk[0]!.length).split(PHCUT)[0] ?? "";
    const cand = takeNameTokens(seg);
    if (looksLikeName(cand)) return applyCase(cand);
  }

  // 3) Phone-adjacency fallback: name token right after (or before) the last phone.
  if (aggressive) {
    const phones = [...note.matchAll(PHONE_G)];
    if (phones.length) {
      const last = phones[phones.length - 1]!;
      const after = note.slice((last.index ?? 0) + last[0]!.length);
      let cand = takeNameTokens(after);
      if (looksLikeName(cand)) return applyCase(cand);

      const segs = note
        .slice(0, last.index ?? 0)
        .split(/[/|,;:#\n()]/)
        .filter((s) => s.trim());
      if (segs.length) {
        const out: string[] = [];
        for (const t of (segs[segs.length - 1] ?? "")
          .split(/\s+/)
          .filter(Boolean)
          .reverse()) {
          const tl = t.toLowerCase().replace(/\.+$/, "");
          if (/\d/.test(t) || STOP.has(tl) || !NAME_TOKEN.test(t)) break;
          out.unshift(t);
          if (out.length >= 2) break;
        }
        cand = out.join(" ").replace(TRIM_EDGES, "");
        if (looksLikeName(cand)) return applyCase(cand);
      }
    }
  }
  return "";
}

// ------------------------------------------------------------------ notes ---
//
// `cleanInvoiceNotes` must survive BOTH note layouts the feed produces: the
// tidy newline-separated "Key: value" form AND one-line blobs where the keys
// sit mid-line (e.g. "epartment: Cardiac Surgery MRN: 3340488 Cost Center: …
// PU Notes: … Requester Email: …", with a dropped leading "D"). So it strips
// labeled metadata segments ANYWHERE — not just at line starts — with each
// value running to the next known key or the line end. PU/DO Notes values +
// free-text prose are preserved; only LABELED `Key: value` metadata, phones,
// and emails are removed.

/** Every recognized label. Drives the value boundary (so a metadata value
 *  stops at the NEXT label, including PU/DO Notes) + the "is this a structured
 *  note?" routing. `D?epartment` tolerates the feed's dropped-leading-char
 *  artifact ("epartment:"); Departament/Deartament/Dept/Dep cover the
 *  (mis)spelling variants. Reservationist / Nr of pass / W are dispatch
 *  metadata seen in slash-delimited dumps. */
const KEY_GROUP =
  "(?:D?epartment|Departament|Deartament|Dept|Dep|MRN|Cost\\s*Center|Requested\\s*By|Requester\\s*Email|Pickup\\s*Location|Reservationist|Nr\\s*of\\s*pass|W|PU\\s*Notes|DO\\s*Notes)";
/** Labels whose whole `Key: value` segment is REMOVED (structured metadata).
 *  PU/DO Notes are deliberately NOT here — their values are human dispatcher
 *  text we keep. Building / Floor / Room are likewise KEPT (useful pickup
 *  context), so they're intentionally absent here too. */
const STRIP_KEY_GROUP =
  "(?:D?epartment|Departament|Deartament|Dept|Dep|MRN|Cost\\s*Center|Requested\\s*By|Requester\\s*Email|Pickup\\s*Location|Reservationist|Nr\\s*of\\s*pass|W)";

/** Any labeled key present? (Reserves the aggressive free-text requester strip
 *  for genuinely unstructured notes, so a PU/DO value with "req…" isn't cut.) */
const ANY_KEY = new RegExp(`\\b${KEY_GROUP}\\s*:`, "i");
/** A metadata `Key → value` segment. The value is lazily consumed up to the
 *  next known key OR the end of the line (`m` flag). No leading `^`, so it
 *  matches mid-line in one-line blobs — and it consumes the WHOLE key token
 *  (incl. the "epartment:" artifact), leaving no partial leftover. */
const META_SEG = new RegExp(
  `\\b${STRIP_KEY_GROUP}\\s*:\\s*.*?(?=\\s*\\b${KEY_GROUP}\\s*:|$)`,
  "gim",
);
/** PU/DO Notes LABEL only — dropped so the human value survives. */
const PUDO_LABEL = /\b(?:PU|DO)\s*Notes\s*:/gi;
/** A leaked email address (e.g. the Requester Email value, incl. @msmc.com). */
const EMAIL_G = /\b[A-Za-z0-9._%+\-]+@[A-Za-z0-9.\-]+\.[A-Za-z]{2,}\b/g;
/** Private sentinel for the "[Return Leg]" marker; survives normalization and
 *  is re-rendered as the prose "Return leg" piece. Never appears in real notes. */
const RETURN_LEG_MARK = "\u0000RL\u0000";
/** A bracket pair whose interior holds NO letters/digits (its metadata / phone /
 *  email was already stripped) — collapsed so "(Req by X 305-…)" doesn't leave
 *  "()"/")". Looped to clear any nesting. Genuine content like "(folding)"
 *  (interior has letters) is left untouched. */
const EMPTY_BRACKETS = /[([{][^A-Za-z0-9([{)\]}]*[)\]}]/g;
/** An isolated / orphaned bracket run with whitespace or a string edge on both
 *  sides (e.g. a lone ")" left after a labeled value was removed). */
const ISOLATED_BRACKETS = /(^|\s)[()[\]{}]+(?=\s|$)/g;
/** Free-text requester marker ("req by" / "booked by|with" / "scheduled by" /
 *  a bare "req"/"req.") followed by its next token (CAPTURED in group 1). The
 *  marker + token are stripped ONLY when that token is a real name — see the
 *  name-gated replace in cleanInvoiceNotes — so a clinical / mobility
 *  instruction ("req w/c", "req O2", "req stretcher") is never consumed.
 *  `\breq\b` can't match "requires"/"request" (no word boundary). */
const REQ_STRIP = /\b(?:requested\s*by|req\.?\s*by|booked\s*(?:by|with)|scheduled\s*by|req\.?)\b[\s:.\-]*([A-Za-z'.\-]*)/gi;
/** Segment delimiters → line breaks: `//`, `|`, the middot `·`, and a
 *  space-padded single `/` (so a "a / b / c" dump splits, but "w/c" / "and/or"
 *  / "6/12" are preserved). */
const DELIMS = /\s*(?:\/\/+|\|+|·)\s*|\s+\/\s+/g;
const TRIM_NOTE = /^[\s.\-/|]+|[\s.\-/|#]+$/g;
/** A phone/contact LABEL left orphaned at the END of a line — its number was
 *  stripped (or it never had one): "Call back:", "ph#", "alternate #", "home:",
 *  etc. EOL-anchored (`m`) + delimiter-required (#/:/“number”) so it ONLY fires
 *  on a trailing, value-less label — never on a mid-line instruction or a bare
 *  word ("call back the desk", "drop at home"). A leading separator
 *  (", " / " · ") is consumed with it. */
const ORPHAN_CONTACT =
  /[\s,;·]*\b(?:ph\s*#|phone\s*(?:#|number)|call\s*back\s*:|callback\s*:|alternate\s*#|alt\s*#|add(?:itiona|t)l?\s*contact\s*(?:#|number)?|contact\s*#|cell\s*(?:#|:)|home\s*:)[\s#:.\-]*$/gim;

function normWs(s: string): string {
  return s.replace(/\s+/g, " ").trim();
}

/**
 * Produce a clean, human-readable note: strip portal/dispatch metadata
 * (Department / MRN / Cost Center / Requested By / Requester Email / Pickup
 * Location), phone numbers + leaked emails; keep the dispatcher's PU/DO/
 * free-text instructions; render "[Return Leg]" as "Return leg". Robust to the
 * tidy multi-line layout AND one-line blobs / mid-line keys. Recovers the
 * CONTENT of n8n col G; it does NOT reproduce n8n's LLM phrasing/recasing.
 *
 * NOTE: a metadata value runs to the next label or the line end (per the feed
 * shape, human content always sits under PU/DO Notes labels), so bare prose on
 * the same line immediately after a metadata value is treated as that value.
 */
export function cleanInvoiceNotes(note: string): string {
  if (!note || !note.trim()) return "";

  // Structured = carries any labeled key (or a return-leg marker). Genuinely
  // free-text notes get the legacy aggressive requester strip; structured ones
  // rely on the colon-anchored `Requested By:` strip in META_SEG so PU/DO text
  // is never clipped.
  const structured = ANY_KEY.test(note) || /\[Return Leg\]/i.test(note);

  // [Return Leg] → a sentinel rendered as its own "Return leg" piece.
  let txt = note.replace(/\[Return Leg\]/gi, `\n${RETURN_LEG_MARK}\n`);

  // Normalize delimiters (//, |, ·, and a space-padded "/") into line breaks
  // FIRST, so a slash/middot-delimited dump becomes one segment per line. Each
  // metadata label is then stripped independently and the genuine content on
  // the OTHER segments (free text, Building/Floor/Room pickup context) survives
  // instead of being swallowed by a metadata value running to the line end.
  txt = txt.replace(DELIMS, "\n");

  // Strip labeled metadata segments wherever they appear (key → value up to the
  // next known key or line end). Handles one-line blobs + the "epartment:"
  // artifact (the full token + value is consumed — no partial leftover).
  txt = txt.replace(META_SEG, " ");

  // Demote PU/DO Notes labels to just their (kept) values.
  txt = txt.replace(PUDO_LABEL, " ");

  // Only for unstructured notes: strip the free-text requester marker + its
  // following NAME ("req by John 305…" → ""). Name-GATED so it never eats a
  // clinical / mobility / instruction word ("req w/c", "req O2", "req
  // stretcher") — those are kept verbatim. This is also what makes the whole
  // cleaner idempotent (a non-name marker is returned unchanged, so a second
  // pass finds nothing new to strip). The colon "Requested By:" form is already
  // gone (META_SEG).
  if (!structured) {
    txt = txt.replace(
      REQ_STRIP,
      (full: string, word: string, offset: number, str: string) => {
        const tl = String(word || "")
          .toLowerCase()
          .replace(/\.+$/u, "");
        const nameLike =
          !!word &&
          looksLikeName(word) &&
          !STOP.has(tl) &&
          !CLINICAL_STOP.has(tl);
        // A marker with no following name but a phone right after it
        // ("req by 305-…") is still a requester marker — strip it too.
        const phoneAdjacent =
          !word &&
          PHONE.test(
            String(str).slice(offset + full.length, offset + full.length + 18),
          );
        return nameLike || phoneAdjacent ? " " : full;
      },
    );
  }

  // Leaked emails + phone numbers, globally.
  txt = txt.replace(EMAIL_G, " ").replace(PHONE_G, " ");

  // Drop a contact LABEL left orphaned at a line end now that its number is
  // gone (or it never had one) — "Call back:", "ph#", "home:", … — so the note
  // doesn't read as bare noise. EOL-anchored + delimiter-required, so a genuine
  // mid-line instruction ("call back the desk", "drop at home") is kept.
  txt = txt.replace(ORPHAN_CONTACT, " ");

  // Clean up brackets the stripping orphaned: collapse now-empty pairs (incl.
  // nesting), then drop isolated/unbalanced bracket runs — so "(Req by X 305-…)"
  // doesn't leave "()"/")". Genuine "(folding)" content is untouched.
  let prev: string;
  do {
    prev = txt;
    txt = txt.replace(EMPTY_BRACKETS, " ");
  } while (txt !== prev);
  txt = txt.replace(ISOLATED_BRACKETS, "$1");

  // Re-assemble: trim each line, KEEP only lines with real content (≥1 letter/
  // digit) — this drops punctuation/bracket-only residue — and join the
  // surviving pieces with ". " (mirrors the prior structured form).
  const cleaned = txt
    .split("\n")
    .map((line) => normWs(line).replace(TRIM_NOTE, ""))
    .map((line) => (line === RETURN_LEG_MARK ? "Return leg" : line))
    .filter((line) => /[A-Za-z0-9]/u.test(line))
    .join(". ");

  // Final-result guard: a result that's only punctuation / brackets / whitespace
  // (e.g. ")", "()", "( )", "-", "//", ".") is noise — return empty.
  return /[A-Za-z0-9]/u.test(cleaned) ? cleaned : "";
}

// ---- Read-only "found in trip note" hints (Invoice Review) ------------------
//
// Pull a candidate Cost Center / MRN from the RAW trip note so the internal,
// read-only Invoice Review can suggest "found in trip note: X — verify and
// enter in TMS". These are display-only HINTS — never billed, never written
// back to the booking. Numeric-only (cost centers + MRNs are digit strings), so
// a junk/non-numeric note value yields "" (no misleading suggestion).

const CC_IN_NOTE = /\bCost\s*Center\s*:\s*(\d{2,10})/i;
const MRN_IN_NOTE = /\bMRN\s*:\s*(\d{3,12})/i;

/** Candidate Cost Center from a raw note ("Cost Center: 7648" → "7648"), or "". */
export function parseCostCenterFromNote(note: string): string {
  return CC_IN_NOTE.exec(note ?? "")?.[1] ?? "";
}

/** Candidate MRN from a raw note ("MRN: 3340488" → "3340488"), or "". */
export function parseMrnFromNote(note: string): string {
  return MRN_IN_NOTE.exec(note ?? "")?.[1] ?? "";
}
